import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import {
  leadSchema,
  type LeadFormValues,
} from "@/forms/schemas/lead.schema";
import { useLeadContext } from "@/forms/useLeadContext";
import { leadsService } from "@/services/leads.service";
import { toApiErrorMessage } from "@/lib/api";

/**
 * Encapsulates all lead-form state: validation, submission, success and error
 * handling. Components stay presentational and simply consume this hook.
 *
 * Also captures CRM-ready origin metadata (source page, industry/service/
 * solution, UTM, referrer, user agent, timestamp) and attaches it to every
 * submission — the visitor never sees or fills these fields.
 */
export function useLeadForm() {
  const [isSuccess, setIsSuccess] = useState(false);
  const { context, sourceLabel } = useLeadContext();

  const form = useForm<LeadFormValues>({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      budget: "",
      service: "",
      message: sourceLabel ? `I'm interested in ${sourceLabel}. ` : "",
    },
    mode: "onBlur",
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await leadsService.submit({ ...values, context });
      setIsSuccess(true);
      form.reset();
      toast.success("Thank you — we'll be in touch within one business day.");
    } catch (error) {
      toast.error(toApiErrorMessage(error));
    }
  });

  const reset = () => {
    setIsSuccess(false);
    form.reset();
  };

  return {
    form,
    onSubmit,
    isSubmitting: form.formState.isSubmitting,
    isSuccess,
    sourceLabel,
    reset,
  };
}
